import React, { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { ColumnConfig } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import JobQueueService from "@app/jobs/job-queues/jobQueueService";
import { JobQueue } from "@app/jobs/job-queues/jobQueue";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import ProjectService from "@app/projects/projectService";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { getMostRecentSaturday, getMostRecentWednesday } from "@app/shared/utility/getMostRecentDay";
import { WeekendRunHungJobsResult } from "@app/operations/jobStatisticsResult";
import OperationsService from "@app/operations/operationsService";

interface WeekendRunHungJobsProps {
  clients: { clientId: number; name: string }[];
  clientId: number | null;
  setClientId: React.Dispatch<React.SetStateAction<number | null>>;
}

const operationsService = new OperationsService();
const projectService = new ProjectService();
const jobQueueService = new JobQueueService();
const WeekendRunHungJobsColumns: ColumnConfig = {
  /* oxlint-disable sort-keys */
  jobId: {
    displayText: "Job Id",
    sizeContentToFit: true,
    testId: "weekend-run-hung-jobs__job-id",
  },
  jobType: {
    displayText: "Job Type",
    sizeContentToFit: true,
    testId: "weekend-run-hung-jobs__job-type",
  },
  regionSetId: {
    displayText: "Region Set Id",
    sizeContentToFit: true,
    testId: "weekend-run-hung-jobs__region-set-id",
  },
  regionCode: {
    displayText: "Region Code",
    sizeContentToFit: true,
    testId: "weekend-run-hung-jobs__region-code",
  },
  projectId: {
    displayText: "Project Id",
    sizeContentToFit: true,
    testId: "weekend-run-hung-jobs__project-id",
  },
  projectRunConfigurationId: {
    displayText: "Project Run Configuration Id",
    sizeContentToFit: true,
    testId: "weekend-run-hung-jobs__project-run-configuration-id",
  },
  errorMessage: {
    displayText: "Error Message",
    sizeContentToFit: true,
    testId: "weekend-run-hung-jobs__error-message",
  },
  jobQueueName: {
    displayText: "Job Queue Name",
    sizeContentToFit: true,
    testId: "weekend-run-hung-jobs__job-queue-name",
  },
  jobQueueSize: {
    displayText: "Job Queue Size",
    sizeContentToFit: true,
    testId: "weekend-run-hung-jobs__job-queue-size",
  }
  /* oxlint-enable sort-keys */
};

const WeekendRunHungJobs: React.FC<WeekendRunHungJobsProps> = ({ clients, clientId, setClientId }) => {
  const isHumana = clients.find((c) => c.clientId === clientId)?.name === "Humana";
  const isUHC = clients.find((c) => c.clientId === clientId)?.name === "United HealthCare Services";
  const [weekendRunHungJobsStats, setWeekendRunHungJobsStats] = useState<WeekendRunHungJobsResult[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [runDate, setRunDate] = useState<string>(isHumana ? getMostRecentSaturday() : getMostRecentWednesday());
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [newQueueName, setNewQueueName] = useState("");
  const [jobQueueList, setJobQueueList] = useState<JobQueue[]>([]);

  const PROJECT_SELECT_ERROR_TEXT = "Disabled";

  useEffect(() => {
    const getQueueList = async () => {
      const queueList = await jobQueueService.getJobQueues();
      setJobQueueList(queueList);
    }
    getQueueList();
    getProjectHungJobs();
  }, []);

  useEffect(() => {
    resetFormValues();
  }, [clientId]);

  useEffect(() => {
    if (selectedProjects.length === 1) {
      const idIndex = Number(selectedProjects[0]);
      const selectedProject = weekendRunHungJobsStats[idIndex];
      setNewQueueName(selectedProject.jobQueueName);
      setJobQueueList((jobQueueList.sort(a => a.name === newQueueName ? -1 : 1)));
    }
    else {
      setNewQueueName("");
    }
  }, [selectedProjects]);

  const resetFormValues = () => {
    setError(null);
    setWeekendRunHungJobsStats(null);
    setSelectedProjects([]);
  }; 

  const getProjectHungJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSelectedProjects([]);
    try {
      if (!!clientId && !!runDate) {
        const response = await operationsService.getHungJobsByClient(
          clientId,
          runDate
        );
        setWeekendRunHungJobsStats(response);
      }
    } catch (error) {
      setError("Error: Incorrect Date or Client");
    } finally {
      setLoading(false);
    }
  }, [clientId, runDate, weekendRunHungJobsStats]);

  const requeueProjectButtonHandler = useCallback(async () => {
    const idIndex = Number(selectedProjects[0]);
    const selectedProject = weekendRunHungJobsStats[idIndex];
    setLoading(true);
    setWeekendRunHungJobsStats(null);

    try {
      await projectService.resubmitProjectJobForClient(
        isHumana,
        isUHC,
        selectedProject.projectId,
        selectedProject.projectRunConfigurationId,
        selectedProject.jobId,
        newQueueName,
        true);

      toast("Job Requeued",
        {
          containerId: "z-index-9999",
          type: "success",
        }
      )
    }
    catch (error) {
      setError("Error: Failed to Resubmit Job");
    }
    finally {
      setSelectedProjects([]);
      setLoading(false);
      getProjectHungJobs();
    }
  }, [selectedProjects, newQueueName, weekendRunHungJobsStats, getProjectHungJobs, isHumana, isUHC]);

  const tableData = useMemo(() => {
    if (weekendRunHungJobsStats) {
      return weekendRunHungJobsStats.map((stat, index) => ({
        /* oxlint-disable sort-keys */
        key: index.toString(),
        jobId: stat.jobId,
        jobType: stat.jobType,
        regionSetId: stat.regionSetId,
        regionCode: stat.regionCode,
        projectId: stat.projectId,
        projectRunConfigurationId: stat.projectRunConfigurationId,
        errorMessage: stat.errorMessage,
        jobQueueName: stat.jobQueueName,
        jobQueueSize: stat.jobQueueSize,
        lastHeartbeatTime: stat.lastHeartbeatTime,
        checkBoxIsDisabled: !stat.projectId || !stat.projectRunConfigurationId
        /* oxlint-enable sort-keys */
      }));
    }
  }, [weekendRunHungJobsStats]);

  const isClientSelectionInvalid = clientId === null;

  let calculateDisabledTooltipText = "";
  if (!runDate || !clientId) {
    calculateDisabledTooltipText = PROJECT_SELECT_ERROR_TEXT;
  }
  let calculateDisabledTooltipRequeueText = "";
  if (loading || selectedProjects.length === 0 || weekendRunHungJobsStats === null || selectedProjects.length > 1) {
    calculateDisabledTooltipRequeueText = PROJECT_SELECT_ERROR_TEXT;
  }

  return (
    <div>
      <div className="form-row mb-3">
        <div className="col pe-1">
          <QESSelect
            label="Client"
            id="clientSelect"
            value={clientId ?? ""}
            onChange={(event) => setClientId(Number(event.target.value))}
            placeholder={clients.length === 0 ? "Loading Clients..." : "Select Client"}
            testId="weekend-run-hung-jobs__client-select"
            required
            disabled={clients.length === 0}
            isValid={!isClientSelectionInvalid}
            errorText={"Invalid Client"}
            positionDescriptionTextAbsolutely={true}
          >
            {clients && clients.map((client) => (
              <option key={client.clientId} value={client.clientId.toString()}>
                {client.name}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col px-1">
          <QESDateInput
            label="Run Date"
            value={runDate}
            onChange={(event) => setRunDate(event.target.value)}
            testId="weekend-run-hung-jobs__run-date-input"
          />
        </div>
        <div className="col d-flex align-items-end ps-1">
          <TooltipWrapper position="top" text={calculateDisabledTooltipText}>
            <QESButton
              disabled={!runDate || !clientId}
              onClick={getProjectHungJobs}
              qesType="primary"
              qesTestId="weekend-run-hung-jobs-statuses__get-hung-jobs-results"
            >
              Search
            </QESButton>
          </TooltipWrapper>
        </div>
      </div>
      <div className="row">
        <div className="col-auto p-0">
          <TooltipWrapper position="top" text={calculateDisabledTooltipRequeueText}>
            <QESButton
              disabled={loading || selectedProjects.length === 0 || weekendRunHungJobsStats === null || selectedProjects.length > 1}
              onClick={requeueProjectButtonHandler}
              className="ms-2 me-1 mt-1"
              qesTestId={"jobs-list__requeue-job-button"}>Requeue Job</QESButton>
          </TooltipWrapper>
        </div>
        <div className="col-auto py-0 ps-1 pe-0">
          <QESSelect
            allowEmpty={true}
            containerClassName="jobs-list__requeue-job-queue-list"
            testId="jobs-list__requeue-list"
            label=""
            placeholder="Select a Queue"
            onChange={(e) => setNewQueueName(e.target.value)}
            disabled={selectedProjects.length !== 1 || weekendRunHungJobsStats === null}
            value={newQueueName} >
            {jobQueueList.map(item => (
              <option key={item.id} value={item.name}>
                {item.name}
              </option>
            ))}
          </QESSelect>
        </div>
      </div>
      <div className="mt-4">
        {loading && <LoadingSpinner />}
        {error && <div>{error}</div>}
        {(weekendRunHungJobsStats && weekendRunHungJobsStats.length > 0) && <div>Displaying Hung Jobs That Have Not Had a Heartbeat In The Last {weekendRunHungJobsStats[0].lastHeartbeatTime} Minutes:</div>}
        {weekendRunHungJobsStats && (
          <Waffles
            columns={WeekendRunHungJobsColumns}
            data={tableData}
            enablePagination={false}
            testId="weekend-run-hung-hung-jobs-table"
            showCheckboxes={true}
            handleCheckboxChange={setSelectedProjects}
            selectedKeys={selectedProjects}
          />
        )}
      </div>
    </div>
  );
};
export default WeekendRunHungJobs;